import type { ValueOf } from 'type-fest';

import { Grid, type BoxProps } from '@frontend/components';

export const SHADOW_FROM_DIRECTION = {
  LEFT: `inset 
  min(var(--overflow-left), var(--blur))
  0
  var(--blur) 
  calc((-1 * var(--blur)) - 2px)
  var(--color)`,
  RIGHT: `inset
  calc(max(-1 * var(--overflow-right), -1 * var(--blur)))
  0
  var(--blur)
  calc((-1 * var(--blur)) - 2px)
  var(--color)
  `,
  TOP: `inset
  0
  min(var(--overflow-top), var(--blur))
  var(--blur)
  calc((-1 * var(--blur)) - 2px)
  var(--color)
  `,
  BOTTOM: `inset
  0
  calc(max(-1 * var(--overflow-bottom), -1 * var(--blur)))
  var(--blur)
  calc((-1 * var(--blur)) - 2px)
  var(--color)
  `,
};

export const REGION = {
  TOP_LEFT: 'sticky-top-left',
  TOP: 'sticky-top',
  TOP_RIGHT: 'sticky-top-right',
  LEFT: 'sticky-left',
  INSIDE: 'inside',
  RIGHT: 'sticky-right',
  BOTTOM_LEFT: 'sticky-bottom-left',
  BOTTOM: 'sticky-bottom',
  BOTTOM_RIGHT: 'sticky-bottom-right',
} as const;

export type Region = ValueOf<typeof REGION>;

type OverscrollShadowContainerProps = BoxProps & {
  overflowBox: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  stickyContentBox: {
    top: number | string;
    right: number | string;
    bottom: number | string;
    left: number | string;
  };
  depth?: number;
  children: React.ReactNode;
};

export type SHADOW_FROM_DIRECTION = keyof typeof SHADOW_FROM_DIRECTION;

/**
 * Creates a CSS grid context that can be used to render shadows that are
 * visible when an element has overscrolled content.
 * Place in an element with position: relative and overflow: hidden.
 * Pair with useOverflowBox() hook to get the overflowBox for the scrollable element.
 **/
export const OverscrollShadowContainer = ({
  overflowBox,
  stickyContentBox,
  depth = 12,
  children,
  ...props
}: OverscrollShadowContainerProps) => {
  const { sx } = props;

  return (
    <Grid
      position="absolute"
      pointerEvents="none"
      aria-hidden="true"
      zIndex={10}
      inset={0}
      style={{
        ['--overflow-top' as string]: overflowBox.top + 'px',
        ['--overflow-right' as string]: overflowBox.right + 'px',
        ['--overflow-bottom' as string]: overflowBox.bottom + 'px',
        ['--overflow-left' as string]: overflowBox.left + 'px',
        ['--depth-unit' as string]: depth + 'px',
        ['--spread' as string]: 'calc((-1 * var(--depth)) - 2px)',
        ['--color' as string]: 'rgba(0,0,0,0.3)',
      }}
      gridTemplate={`
        "${REGION.TOP_LEFT} ${REGION.TOP} ${REGION.TOP_RIGHT}" ${
        stickyContentBox.top ? stickyContentBox.top : 'auto'
      } 
        "${REGION.LEFT} ${REGION.INSIDE} ${REGION.RIGHT}" 1fr
        "${REGION.BOTTOM_LEFT} ${REGION.BOTTOM} ${REGION.BOTTOM_RIGHT}" ${
        stickyContentBox.bottom ? stickyContentBox.bottom : 'auto'
      }
        / ${stickyContentBox.left ? stickyContentBox.left : 'auto'} 1fr ${
        stickyContentBox.right ? stickyContentBox.right : 'auto'
      }
      `}
      sx={{
        '*': {
          position: 'absolute',
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Grid>
  );
};
